import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Save, Film, Download, RotateCcw, Loader2, Clock, Plus, Maximize, Minimize, Keyboard, Trash2 } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ScrollArea } from "./ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CueBadges, EditorMetricsStrip, cueHasBadges } from "./CueBadges";
import { toast } from "sonner";
import { RenderProgress } from "./RenderProgress";

function fmt(s) {
  const t = Math.max(0, s || 0);
  const m = Math.floor(t / 60);
  const ss = Math.floor(t % 60);
  const ms = Math.floor((t - Math.floor(t)) * 100);
  return `${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}.${String(ms).padStart(2, "0")}`;
}

export default function VideoEditor({ job, onJobUpdated }) {
  const { user } = useAuth();
  const [segments, setSegments] = useState(() => job.segments.map((s) => ({ ...s })));
  const initialRef = useRef(JSON.stringify(job.segments));
  const [dirty, setDirty] = useState(false);
  const [planKey, setPlanKey] = useState(null);

  // Lightweight one-shot lookup: is this user on the free tier?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/usage");
        if (!cancelled) setPlanKey(data?.plan?.key || null);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("plan lookup failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [renderOutputHeight, setRenderOutputHeight] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRenderOutputHeight(null);
    api.get("/render-settings").then(({ data }) => {
      if (!cancelled) setRenderOutputHeight(data.output_height);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [job.job_id]);

  const willWatermark = planKey === "free";
  const [currentIdx, setCurrentIdx] = useState(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [renderStatus, setRenderStatus] = useState(job.render_status || (job.has_rendered_video ? "done" : "idle"));
  const [renderProgress, setRenderProgress] = useState(job.render_progress || null);
  const [renderId, setRenderId] = useState(job.render_id || null);
  const [renderExpiresAt, setRenderExpiresAt] = useState(job.render_expires_at || null);
  const pollGeneration = useRef(0);
  const pollSequence = useRef(0);
  const renderMutation = useRef(false);
  const invalidatePolls = useCallback(() => { pollGeneration.current += 1; }, []);
  const [saving, setSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const pollRef = useRef(null);

  // Track fullscreen state on the wrapper (custom fullscreen keeps the
  // subtitle overlay inside the fullscreen element — the native <video>
  // fullscreen button would leave the overlay behind in normal DOM flow).
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  const mediaUrl = useMemo(
    () => `${API}/subtitles/${job.job_id}/media`,
    [job.job_id]
  );

  // Build a WebVTT blob URL from the current segments so we can attach it
  // to the <video> element via a native <track>. This is what makes the
  // captions render inside the video itself — the browser then draws them
  // in EVERY fullscreen path (native button, F key, double-click, PiP,
  // mobile fullscreen) without us needing to intercept anything.
  //
  // We rebuild the blob whenever `segments` change (translations edited,
  // segments inserted/deleted, timestamps shifted). Each rebuild revokes
  // the previous URL so we don't leak memory when the user edits a lot.
  const vttUrl = useMemo(() => {
    const fmt = (t) => {
      const s = Math.max(0, Number(t) || 0);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      // WebVTT timestamps use `.` (not `,`) between seconds and millis.
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${sec.toFixed(3).padStart(6, "0")}`;
    };
    const lines = ["WEBVTT", ""];
    for (const s of segments) {
      const text = (s.translated_text || "").replace(/\s+/g, " ").trim();
      if (!text || s.end <= s.start) continue;
      lines.push(`${fmt(s.start)} --> ${fmt(s.end)}`);
      lines.push(text);
      lines.push("");
    }
    const blob = new Blob([lines.join("\n")], { type: "text/vtt" });
    return URL.createObjectURL(blob);
  }, [segments]);

  // Free the blob URL when we swap in a new one, and on unmount. Without
  // this, editing a long transcript over minutes can pin dozens of MB in
  // memory (each blob is small but they add up).
  useEffect(() => () => URL.revokeObjectURL(vttUrl), [vttUrl]);

  // Force the on-video captions to be VISIBLE. Browsers respect the OS
  // "captions off" setting by default, which would silently hide our
  // preview even though the <track> was loaded correctly — a huge foot-gun
  // during editing. Setting mode='showing' overrides that.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const applyMode = () => {
      const tracks = v.textTracks || [];
      for (let i = 0; i < tracks.length; i++) tracks[i].mode = "showing";
    };
    applyMode();
    // Track gets re-attached whenever vttUrl changes, so listen for the
    // addtrack event and re-apply mode.
    v.textTracks?.addEventListener?.("addtrack", applyMode);
    return () => v.textTracks?.removeEventListener?.("addtrack", applyMode);
  }, [vttUrl]);

  // Re-sync when the parent hands us a new job (open from history, patch
  // reply, etc.). We KEY on job_id + edited_at + has_rendered_video rather
  // than on the segments array itself — the parent replaces the object on
  // every save, and re-mounting `segments` on each save would race with
  // in-flight local edits. edited_at only changes on save so it captures
  // "the segments actually changed on the server".
  useEffect(() => {
    setSegments(job.segments.map((s) => ({ ...s })));
    initialRef.current = JSON.stringify(job.segments);
    setDirty(false);
    setRenderStatus(job.render_status || (job.has_rendered_video ? "done" : "idle"));
    setRenderId(job.render_id || null);
    setRenderProgress(job.render_progress || null);
    setRenderExpiresAt(job.render_expires_at || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.job_id, job.edited_at, job.has_rendered_video, job.render_id]);

  // Update dirty flag whenever segments change
  useEffect(() => {
    setDirty(JSON.stringify(segments) !== initialRef.current);
  }, [segments]);

  // Track currently-playing segment
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      const t = v.currentTime;
      const idx = segments.findIndex((s) => t >= s.start && t <= s.end);
      setCurrentIdx(idx >= 0 ? idx : null);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [segments]);

  // Auto-scroll the active segment into view as playback advances.
  //
  // UX rules:
  //   1. `block: 'center'` — keeps the active line vertically centered so
  //      the user can preview the two lines above/below without shifting
  //      their eyes to a corner.
  //   2. Manual scroll pause — if the user scrolls the segment list by
  //      hand, we suppress auto-scroll for 4 seconds so the auto-scroll
  //      doesn't yank them back mid-edit. Any typing inside a textarea is
  //      *not* treated as scrolling (Textarea keydown doesn't fire the
  //      container's `scroll` event).
  //   3. Uses the container's own scrollTop instead of scrollIntoView, so
  //      the outer page scroll never jumps — a common bug with default
  //      scrollIntoView inside a nested overflow container.
  const listContainerRef = useRef(null);
  const userScrollPauseUntilRef = useRef(0);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      // Debounced flag — set on every scroll, cleared 100 ms after the
      // last scroll so programmatic (auto-scroll) motion doesn't self-
      // trigger this handler in a loop.
      userScrollPauseUntilRef.current = Date.now() + 4000;
    };
    // Shadcn ScrollArea's actual scrollable element is the child viewport,
    // not the wrapper. Look it up.
    const viewport = container.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) || container;
    viewport.addEventListener("wheel", onScroll, { passive: true });
    viewport.addEventListener("touchmove", onScroll, { passive: true });
    return () => {
      viewport.removeEventListener("wheel", onScroll);
      viewport.removeEventListener("touchmove", onScroll);
    };
  }, []);

  useEffect(() => {
    if (currentIdx == null) return;
    if (Date.now() < userScrollPauseUntilRef.current) return;
    const container = listContainerRef.current;
    if (!container) return;
    const viewport = container.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) || container;
    // Find the active segment DOM node by its stable testid.
    const displayIdx = segments[currentIdx]?.index ?? (currentIdx + 1);
    const target = viewport.querySelector(
      `[data-testid="editor-segment-${displayIdx}"]`
    );
    if (!target) return;
    // Compute the desired scrollTop that centers `target` in the viewport.
    // We compute it manually (rather than `scrollIntoView`) so the outer
    // page never scrolls sideways — nested overflow scrolls do that with
    // the default option set.
    const viewportRect = viewport.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const currentOffset =
      targetRect.top - viewportRect.top + viewport.scrollTop;
    const desiredScrollTop =
      currentOffset - viewport.clientHeight / 2 + target.clientHeight / 2;
    viewport.scrollTo({
      top: Math.max(0, desiredScrollTop),
      behavior: "smooth",
    });
  }, [currentIdx, segments]);

  // ------------ seekTo (declared early so keydown handler can close over it) ------------
  const seekTo = useCallback(async (idx) => {
    const v = videoRef.current;
    if (!v) return;
    const s = segments[idx];
    if (!s) return;
    const target = Math.max(0, Number(s.start) + 0.05);
    // For long videos the metadata may not be loaded yet — wait until the
    // browser knows the duration before seeking, otherwise the assignment
    // silently no-ops.
    if (v.readyState < 1) {
      await new Promise((r) => {
        const done = () => { v.removeEventListener("loadedmetadata", done); r(); };
        v.addEventListener("loadedmetadata", done);
        setTimeout(done, 3000);
      });
    }
    v.pause();
    try {
      v.currentTime = target;
    } catch {
      v.currentTime = Math.min(target, (v.duration || target) - 0.1);
    }
    setCurrentIdx(idx);
  }, [segments]);

  // ------------ Undo history ------------
  // Snapshots the segments array on user-driven changes so Ctrl+Z can step
  // back. Debounced by 400 ms so rapid typing collapses into one undo step
  // (matches VS Code, Sublime, etc.). Skips programmatic updates (revert
  // / job reload) via the `skipHistoryRef` flag. Capped at 50 entries.
  const historyRef = useRef([]); // stack of prior segment states
  const skipHistoryRef = useRef(true); // true → don't record next change
  const lastPushRef = useRef(0);
  useEffect(() => {
    if (skipHistoryRef.current) {
      skipHistoryRef.current = false;
      return;
    }
    const snapshot = JSON.stringify(segments);
    const prev = historyRef.current[historyRef.current.length - 1];
    if (prev === snapshot) return;
    const now = Date.now();
    // Coalesce rapid successive edits within 400 ms into a single entry —
    // otherwise typing "hello" would take 5 Ctrl+Z presses to undo.
    if (now - lastPushRef.current < 400 && historyRef.current.length > 0) {
      historyRef.current[historyRef.current.length - 1] = snapshot;
    } else {
      historyRef.current.push(snapshot);
      if (historyRef.current.length > 51) historyRef.current.shift();
    }
    lastPushRef.current = now;
  }, [segments]);
  // Whenever a fresh job is opened, wipe the undo stack — otherwise Ctrl+Z
  // could take the user back to a completely unrelated job's state.
  useEffect(() => {
    historyRef.current = [];
    skipHistoryRef.current = true;
    lastPushRef.current = 0;
  }, [job.job_id]);

  const undo = useCallback(() => {
    // We need to pop TWO — the most recent snapshot is the current state,
    // so restore the one BEFORE that. If the stack has only one entry,
    // we simply pop it and restore to the initial job state.
    if (historyRef.current.length < 2) {
      if (historyRef.current.length === 1) {
        historyRef.current.pop();
        skipHistoryRef.current = true;
        setSegments(job.segments.map((s) => ({ ...s })));
      }
      return;
    }
    historyRef.current.pop();
    const restore = historyRef.current[historyRef.current.length - 1];
    skipHistoryRef.current = true;
    setSegments(JSON.parse(restore));
  }, [job.segments]);

  // ------------ Keyboard helpers ------------
  // Focus a segment's textarea by display index. Used by nav shortcuts and
  // Enter's split action so keyboard flow never leaves the list.
  const focusSegmentTextarea = useCallback((displayIdx) => {
    // Give React one frame to render the (possibly-newly-inserted) segment.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="edit-text-${displayIdx}"]`);
      if (el && typeof el.focus === "function") {
        el.focus();
        // Place caret at end for a clean editing continuation.
        if (typeof el.setSelectionRange === "function") {
          const len = el.value?.length ?? 0;
          try { el.setSelectionRange(len, len); } catch { /* noop */ }
        }
      }
    });
  }, []);

  // Split the active segment at the textarea's cursor position. First half
  // stays with the current segment (start unchanged, end proportional to
  // text length), second half becomes a brand new segment inserted right
  // after with the remainder of the text.
  const splitSegmentAtCursor = useCallback((idx, cursorPos) => {
    setSegments((prev) => {
      const s = prev[idx];
      if (!s) return prev;
      const text = String(s.translated_text ?? "");
      const cur = Math.min(Math.max(0, cursorPos ?? text.length), text.length);
      const left = text.slice(0, cur).trimEnd();
      const right = text.slice(cur).trimStart();
      // Split the timespan proportionally to text length so the split feels
      // "in the right place" even if the two halves are lopsided.
      const dur = Math.max(0.05, Number(s.end) - Number(s.start));
      const ratio = text.length > 0 ? cur / text.length : 0.5;
      const midTime = Number(s.start) + dur * (ratio > 0 && ratio < 1 ? ratio : 0.5);
      const first = { ...s, translated_text: left, end: Number(midTime.toFixed(3)) };
      const second = {
        ...s,
        index: 0,
        translated_text: right,
        source_text: "",
        start: Number(midTime.toFixed(3)),
        end: Number(s.end),
      };
      const out = [...prev.slice(0, idx), first, second, ...prev.slice(idx + 1)];
      return out.map((seg, i) => ({ ...seg, index: i + 1 }));
    });
    // Focus the second half so the user can keep typing where they left off.
    focusSegmentTextarea(idx + 2);
  }, [focusSegmentTextarea]);

  // Remove segment at idx. Renumbers the rest.
  const removeSegment = useCallback((idx) => {
    setSegments((prev) => {
      if (prev.length <= 1) return prev; // never leave the editor with zero cues
      const out = [...prev.slice(0, idx), ...prev.slice(idx + 1)];
      return out.map((s, i) => ({ ...s, index: i + 1 }));
    });
    toast.info("Segment removed — Ctrl+Z to undo");
  }, []);

  // Nudge start/end times by delta seconds (positive or negative). Clamps to
  // 0 and to the next/prev segment's boundary so we can't create overlaps.
  const NUDGE_STEP = 0.1;
  const nudgeStart = useCallback((idx, delta) => {
    setSegments((prev) => {
      const s = prev[idx];
      if (!s) return prev;
      const prevEnd = idx > 0 ? Number(prev[idx - 1].end) : 0;
      const nextStart = Math.min(Number(s.end) - 0.05, Number(s.start) + delta);
      const clamped = Math.max(prevEnd, nextStart);
      return prev.map((x, i) => (i === idx ? { ...x, start: Number(clamped.toFixed(3)) } : x));
    });
  }, []);
  const nudgeEnd = useCallback((idx, delta) => {
    setSegments((prev) => {
      const s = prev[idx];
      if (!s) return prev;
      const nextStart = idx < prev.length - 1 ? Number(prev[idx + 1].start) : Infinity;
      const proposed = Math.max(Number(s.start) + 0.05, Number(s.end) + delta);
      const clamped = Math.min(nextStart, proposed);
      return prev.map((x, i) => (i === idx ? { ...x, end: Number(clamped.toFixed(3)) } : x));
    });
  }, []);

  // ------------ Global keydown handler ------------
  // Attaches at document level so it works no matter which textarea the
  // user is typing in. Every shortcut resolves the "current segment index"
  // from either the focused textarea's testid or the currently-playing
  // segment (in that order of precedence).
  const [showShortcuts, setShowShortcuts] = useState(false);
  useEffect(() => {
    const editorEl = document.querySelector('[data-testid="video-editor"]');
    if (!editorEl) return;

    const activeIndexFromEvent = (e) => {
      // Priority 1 — the textarea the user's currently in, resolved via
      // its testid `edit-text-${displayIdx}`.
      const t = e.target;
      const m = t?.getAttribute?.("data-testid")?.match?.(/^edit-text-(\d+)$/);
      if (m) {
        const displayIdx = Number(m[1]);
        const idx = segments.findIndex((s) => (s.index ?? -1) === displayIdx);
        if (idx >= 0) return idx;
      }
      // Priority 2 — whichever segment the video is currently playing.
      if (currentIdx != null) return currentIdx;
      return null;
    };

    const inEditor = (t) => editorEl.contains(t);

    const onKeyDown = (e) => {
      if (!inEditor(e.target)) return;
      // Never intercept when a modifier that we don't own is held.
      const mod = e.ctrlKey || e.metaKey;

      // Undo — Ctrl/Cmd + Z (no Shift; we don't implement redo yet)
      if (mod && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }
      if (mod) return; // let other Ctrl-chords through untouched

      const idx = activeIndexFromEvent(e);

      // Navigation ----------------------------------------------------------
      if (e.key === "ArrowUp") {
        if (idx == null) return;
        const next = Math.max(0, idx - 1);
        e.preventDefault();
        const disp = segments[next]?.index ?? (next + 1);
        seekTo(next);
        focusSegmentTextarea(disp);
        return;
      }
      if (e.key === "ArrowDown") {
        if (idx == null) return;
        const next = Math.min(segments.length - 1, idx + 1);
        e.preventDefault();
        const disp = segments[next]?.index ?? (next + 1);
        seekTo(next);
        focusSegmentTextarea(disp);
        return;
      }
      if (e.key === "Tab") {
        // Tab / Shift+Tab walk segments; still works inside textarea.
        if (idx == null) return;
        e.preventDefault();
        const dir = e.shiftKey ? -1 : 1;
        const next = Math.max(0, Math.min(segments.length - 1, idx + dir));
        const disp = segments[next]?.index ?? (next + 1);
        seekTo(next);
        focusSegmentTextarea(disp);
        return;
      }

      // Playback ------------------------------------------------------------
      if (e.key === " " || e.code === "Space") {
        // Shift+Space → replay current segment from its start
        if (e.shiftKey) {
          if (idx == null) return;
          e.preventDefault();
          const v = videoRef.current;
          if (!v) return;
          v.currentTime = Math.max(0, Number(segments[idx].start) + 0.01);
          v.play().catch(() => {});
          return;
        }
        // Plain Space → toggle play/pause. Intercepts even inside a Textarea
        // (per spec). Users type spaces in captions via clipboard paste if
        // needed; escape-hatch documented in the shortcuts modal.
        e.preventDefault();
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) v.play().catch(() => {}); else v.pause();
        return;
      }
      // Only fire j/l/[/] when NOT in the middle of typing a modifier chord.
      const k = e.key.toLowerCase();
      if (k === "j") {
        e.preventDefault();
        const v = videoRef.current;
        if (v) v.currentTime = Math.max(0, v.currentTime - 2);
        return;
      }
      if (k === "l") {
        e.preventDefault();
        const v = videoRef.current;
        if (v) v.currentTime = Math.min((v.duration || Infinity), v.currentTime + 2);
        return;
      }

      // Timing shifts -------------------------------------------------------
      if (e.shiftKey && e.key === "{") {
        // Shift+[ on US layout produces "{" — extend start earlier
        if (idx == null) return;
        e.preventDefault();
        nudgeStart(idx, -NUDGE_STEP);
        return;
      }
      if (e.shiftKey && e.key === "}") {
        // Shift+] → extend end later
        if (idx == null) return;
        e.preventDefault();
        nudgeEnd(idx, +NUDGE_STEP);
        return;
      }

      // Editing -------------------------------------------------------------
      if (e.key === "Enter" && !e.shiftKey) {
        // Only inside a segment textarea: split at cursor.
        const t = e.target;
        const isSegmentTextarea =
          t?.tagName === "TEXTAREA" && /^edit-text-\d+$/.test(t.getAttribute("data-testid") || "");
        if (!isSegmentTextarea || idx == null) return;
        e.preventDefault();
        const cursor = typeof t.selectionStart === "number" ? t.selectionStart : t.value.length;
        splitSegmentAtCursor(idx, cursor);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        // Delete an empty segment. Fires only when the textarea is EMPTY
        // so users can still delete individual characters normally.
        const t = e.target;
        const isSegmentTextarea =
          t?.tagName === "TEXTAREA" && /^edit-text-\d+$/.test(t.getAttribute("data-testid") || "");
        if (!isSegmentTextarea || idx == null) return;
        if ((t.value ?? "").length > 0) return; // let default delete a char
        e.preventDefault();
        removeSegment(idx);
        // Focus the previous segment's textarea for continuity.
        const focusOn = Math.max(0, idx - 1);
        const disp = segments[focusOn]?.index ?? (focusOn + 1);
        focusSegmentTextarea(disp);
        return;
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [segments, currentIdx, seekTo, undo, splitSegmentAtCursor, removeSegment, nudgeStart, nudgeEnd, focusSegmentTextarea]);

  // ------------ Undo history ------------

  const updateSegment = (idx, patch) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  // Insert one or more brand-new empty segments anywhere on the timeline —
  // BEFORE the first (`insertAfterIdx = -1`), between two adjacent
  // segments, or AFTER the last. Multiple inserts fan out in the gap so
  // they don't overlap each other or their neighbours.
  const insertSegments = (insertAfterIdx, count = 1) => {
    const n = Math.max(1, Math.min(10, count | 0));
    setSegments((prev) => {
      const prevSeg = insertAfterIdx >= 0 ? prev[insertAfterIdx] : null;
      const nextSeg = prev[insertAfterIdx + 1] || null;
      const videoDuration = videoRef.current?.duration || 0;
      // Available window:
      //   - before-first: 0 → prev[0].start (or +3s if no video)
      //   - between:      prevSeg.end → nextSeg.start
      //   - after-last:   prevSeg.end → videoDuration (or prevSeg.end + n*2s)
      const gapStart = prevSeg ? Number(prevSeg.end) : 0;
      const gapEnd = nextSeg
        ? Number(nextSeg.start)
        : (videoDuration > gapStart + 0.5 ? videoDuration : gapStart + n * 2.0);
      const gap = Math.max(0, gapEnd - gapStart);
      // Slice the gap into n roughly-equal windows with 0.1 s guard rails
      // between siblings.
      const inserted = [];
      const guard = 0.1;
      const per = gap >= 0.6 * n
        ? (gap - guard * (n - 1)) / n
        : 1.2; // no real gap → each new seg is a 1.2 s carve-out
      for (let i = 0; i < n; i++) {
        const base = gap >= 0.6 * n
          ? gapStart + i * (per + guard)
          : gapStart + i * (per + guard); // same formula; neighbours will overlap slightly if no gap — user can nudge
        inserted.push({
          index: 0, // renumbered below
          start: Number(base.toFixed(3)),
          end: Number((base + per).toFixed(3)),
          source_text: "",
          translated_text: "",
          speaker: null,
          cps: 0,
          lines: 0,
          qc_flags: [],
        });
      }
      const anchor = insertAfterIdx + 1; // where to splice in
      const next = [...prev.slice(0, anchor), ...inserted, ...prev.slice(anchor)];
      return next.map((s, i) => ({ ...s, index: i + 1 }));
    });
    setTimeout(() => {
      const focusIdx = insertAfterIdx + 2; // display index (1-based) of the first inserted
      const el = document.querySelector(`[data-testid="edit-text-${focusIdx}"]`);
      if (el && typeof el.focus === "function") el.focus();
    }, 50);
    toast.info(
      n === 1
        ? "Blank segment inserted — type the missed dialogue."
        : `Inserted ${n} blank segments in the gap.`
    );
  };

  // Back-compat alias for the old single-insert entry point.
  const insertSegmentAfter = (idx) => insertSegments(idx, 1);

  const revert = () => {
    setSegments(job.segments.map((s) => ({ ...s })));
    initialRef.current = JSON.stringify(job.segments);
    setDirty(false);
    toast.info("Reverted to last saved");
  };

  const save = async () => {
    const generation = ++pollGeneration.current;
    renderMutation.current = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setSaving(true);
    let saved = false;
    try {
      const payload = {
        segments: segments.map((s) => ({
          index: s.index,
          start: Number(s.start),
          end: Number(s.end),
          translated_text: s.translated_text,
        })),
      };
      const { data } = await api.patch(`/subtitles/${job.job_id}`, payload);
      if (generation !== pollGeneration.current) return;
      saved = true;
      // Reset the dirty baseline so Save disables and Render becomes available.
      initialRef.current = JSON.stringify(segments);
      setDirty(false);
      setRenderStatus(data.has_rendered_video ? "done" : "idle");
      setRenderId(data.render_id || null);
      setRenderProgress(data.render_progress || null);
      setRenderExpiresAt(data.render_expires_at || null);
      onJobUpdated?.(data);
      toast.success("Subtitles saved");
    } catch (e) {
      if (generation === pollGeneration.current) toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      if (generation === pollGeneration.current) {
        renderMutation.current = false;
        setSaving(false);
        if (!saved) {
          const data = await pollStatus();
          if (generation === pollGeneration.current && ["queued", "running"].includes(data?.status)) {
            pollRef.current = setInterval(pollStatus, 2500);
          }
        }
      }
    }
  };

  const pollStatus = useCallback(async () => {
    if (renderMutation.current) return null;
    const generation = pollGeneration.current;
    const sequence = ++pollSequence.current;
    try {
      const { data } = await api.get(`/subtitles/${job.job_id}/render-status`);
      if (generation !== pollGeneration.current || sequence !== pollSequence.current) return null;
      setRenderStatus(data.status);
      setRenderProgress(data.progress || null);
      setRenderId(data.render_id || null);
      setRenderExpiresAt(data.expires_at || null);
      if (!["queued", "running"].includes(data.status)) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
      if (data.status === "failed" && data.error) toast.error(`Render failed: ${data.error}`);
      return data;
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Poll status failed:", err?.message);
      return null;
    }
  }, [job.job_id]);

  const startRender = async () => {
    if (dirty) {
      toast.info("Save your edits first.");
      return;
    }
    if (renderMutation.current) return;
    const generation = ++pollGeneration.current;
    renderMutation.current = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setRenderStatus("queued");
    setRenderProgress({ percent: 0, stage: "Queued", eta_seconds: null });
    setRenderExpiresAt(null);
    try {
      const { data } = await api.post(`/subtitles/${job.job_id}/render-video`, { style });
      if (generation !== pollGeneration.current) return;
      renderMutation.current = false;
      setRenderId(data.render_id || null);
      setRenderStatus(data.status);
      setRenderProgress(data.progress || null);
      setRenderExpiresAt(data.expires_at || null);
      if (["queued", "running"].includes(data.status)) {
        pollRef.current = setInterval(pollStatus, 2500);
        await pollStatus();
      }
    } catch (e) {
      if (generation !== pollGeneration.current) return;
      toast.error(e?.response?.data?.detail || "Could not start render");
      setRenderStatus("idle");
    } finally {
      if (generation === pollGeneration.current) renderMutation.current = false;
    }
  };

  useEffect(() => {
    invalidatePolls();
    renderMutation.current = false;
    setSaving(false);
    let active = true;
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    const refresh = () => pollStatus().then((data) => {
      if (active && data && ["queued", "running"].includes(data.status) && !pollRef.current) {
        pollRef.current = setInterval(pollStatus, 2500);
      }
    });
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      active = false;
      invalidatePolls();
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      window.removeEventListener("focus", refresh);
    };
  }, [job.job_id, pollStatus, invalidatePolls]);

  useEffect(() => {
    const deadline = Date.parse(renderExpiresAt);
    if (renderStatus !== "done" || !Number.isFinite(deadline)) return;
    const timer = setTimeout(() => setRenderStatus("expired"), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [renderStatus, renderExpiresAt]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  const hasVideoMedia = ["video", "youtube", "instagram", "tiktok"].includes(job.input_type);

  const [style, setStyle] = useState(() => ({
    font: "Arial",
    size: 22,
    color: "#FFFFFF",
    outline_color: "#000000",
    background: "outline",         // "none" | "outline" | "box"
    background_color: "#000000",
    background_opacity: 60,
    outline: 2,
    position: "bottom",             // "top" | "middle" | "bottom"
  }));
  const [styleOpen, setStyleOpen] = useState(false);

  return (
    <section className="border border-zinc-800 bg-zinc-900/40" data-testid="video-editor">
      <div className="p-6 md:p-8 border-b border-zinc-800 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
            <Film className="w-3 h-3" /> Edit & render
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-white">
            Fine-tune subtitles on the video.
          </h2>
          <p className="text-xs text-zinc-500 mt-2 font-mono">
            Click any line to jump to it · the active line auto-scrolls into view · edits save server-side · original media auto-deleted after 48 h
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowShortcuts(true)}
          data-testid="editor-shortcuts-btn"
          aria-label="Show keyboard shortcuts"
          title="Keyboard shortcuts (13)"
          className="self-start md:self-end inline-flex items-center gap-2 h-9 px-3 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800/60 text-zinc-400 hover:text-white transition-colors duration-150 text-[10px] font-mono uppercase tracking-widest"
        >
          <Keyboard className="w-3.5 h-3.5" />
          Shortcuts
        </button>
      </div>

      <ShortcutsModal open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-0">
        {/* Video — sticky on lg+ so it stays visible while the user scrolls
            the segment list. Top offset accounts for the fixed nav (h-16). */}
        <div className="p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-zinc-800 lg:sticky lg:top-16 lg:self-start lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">
          {hasVideoMedia ? (
            <div
              ref={wrapperRef}
              className="relative bg-black aspect-video group/videowrap"
              data-testid="video-player-wrap"
            >
              <video
                ref={videoRef}
                data-testid="video-player"
                controls
                preload="metadata"
                src={mediaUrl}
                className="w-full h-full"
              >
                {/* Native <track> — the browser renders these cues INSIDE
                    the video element itself, so they follow the video into
                    every fullscreen path (native button, F key, double-click,
                    PiP, mobile) without any custom overlay gymnastics.
                    Keyed on the blob URL so React re-mounts (and the browser
                    re-parses) whenever the transcript is edited. */}
                <track
                  key={vttUrl}
                  src={vttUrl}
                  kind="subtitles"
                  srcLang="en"
                  label="Subtitles"
                  default
                />
              </video>
              {/* Custom fullscreen toggle — still useful on mobile where
                  the native controls fullscreen button can be hard to hit.
                  Positioned bottom-right, fades in on hover. */}
              <button
                type="button"
                onClick={toggleFullscreen}
                data-testid="video-fullscreen-btn"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                className="absolute bottom-2 right-2 z-20 h-8 w-8 flex items-center justify-center bg-black/70 hover:bg-black/90 text-white rounded-sm opacity-0 group-hover/videowrap:opacity-100 focus:opacity-100 transition-opacity"
              >
                {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
              </button>
            </div>
          ) : (
            <div className="border border-zinc-800 bg-zinc-950 p-6 text-sm text-zinc-500">
              Editor works with video jobs. For audio or text you can still edit the subtitles below and re-download SRT/VTT.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {hasVideoMedia && (
              <Button
                type="button"
                onClick={togglePlay}
                data-testid="editor-playpause-btn"
                className="h-9 px-3 border border-zinc-800 bg-transparent hover:bg-zinc-800 text-white rounded-none text-xs font-mono uppercase tracking-widest"
              >
                {isPlaying ? <Pause className="w-3 h-3 mr-1.5" /> : <Play className="w-3 h-3 mr-1.5" />}
                {isPlaying ? "Pause" : "Play"}
              </Button>
            )}
            <Button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              data-testid="editor-save-btn"
              className="h-9 px-4 bg-white text-zinc-950 hover:bg-zinc-200 disabled:bg-zinc-800 disabled:text-zinc-500 rounded-none text-xs font-mono uppercase tracking-widest inline-flex items-center"
            >
              <Save className="w-3 h-3 mr-1.5" /> {saving ? "Saving..." : "Save edits"}
            </Button>
            {dirty && (
              <Button
                type="button"
                onClick={revert}
                data-testid="editor-revert-btn"
                className="h-9 px-3 border border-zinc-800 bg-transparent hover:bg-zinc-800 text-zinc-300 rounded-none text-xs font-mono uppercase tracking-widest inline-flex items-center"
              >
                <RotateCcw className="w-3 h-3 mr-1.5" /> Revert
              </Button>
            )}
            {hasVideoMedia && (
              <div className="ml-auto flex items-center gap-2" data-testid="render-controls">
                <Button
                  type="button"
                  onClick={() => setStyleOpen((v) => !v)}
                  data-testid="editor-style-toggle"
                  className="h-9 px-3 border border-zinc-800 bg-transparent hover:bg-zinc-800 text-zinc-300 rounded-none text-xs font-mono uppercase tracking-widest inline-flex items-center"
                >
                  Caption style
                </Button>
                {renderStatus === "done" ? (
                  <a
                    href={`${API}/subtitles/${job.job_id}/download-video${renderId ? `?render_id=${encodeURIComponent(renderId)}` : ""}`}
                    data-testid="download-subtitled-video-btn"
                    className="h-9 px-4 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 rounded-none text-xs font-mono uppercase tracking-widest inline-flex items-center font-semibold"
                  >
                    <Download className="w-3 h-3 mr-1.5" /> Download subtitled video
                  </a>
                ) : (
                  <Button
                    type="button"
                    onClick={startRender}
                    disabled={["queued", "running"].includes(renderStatus)}
                    data-testid="editor-render-btn"
                    className="h-9 px-4 bg-blue-500 text-white hover:bg-blue-400 disabled:bg-blue-900 rounded-none text-xs font-mono uppercase tracking-widest inline-flex items-center font-semibold"
                  >
                    {["queued", "running"].includes(renderStatus) ? (
                      <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Rendering...</>
                    ) : (
                      <><Film className="w-3 h-3 mr-1.5" /> Render subtitled video</>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
          {hasVideoMedia && ["queued", "running"].includes(renderStatus) && (
            <RenderProgress progress={renderProgress} queued={renderStatus === "queued"} />
          )}
          {hasVideoMedia && renderOutputHeight === 720 && (
            <p data-testid="render-output-quality-notice" className="mt-3 text-xs font-mono text-zinc-400 leading-relaxed">
              Rendered video output is limited to {renderOutputHeight}p.
            </p>
          )}
          {hasVideoMedia && renderStatus === "done" && (
            <p data-testid="render-download-expiry-notice" role="status" className="mt-3 text-xs font-mono text-zinc-400 leading-relaxed">
              Rendered videos are available for 4 hours after rendering, then automatically removed. Please download promptly.
            </p>
          )}
          {willWatermark && renderStatus !== "done" && (
            <div
              data-testid="watermark-note"
              className="mt-3 text-[11px] font-mono text-zinc-500 border border-zinc-800 bg-zinc-950/60 px-3 py-2 leading-relaxed"
            >
              <span className="text-amber-300">Free tier:</span> a subtle
              "Made with LinguaSub" watermark is baked into the bottom-right
              of your rendered video. <a
                href="/pricing"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Upgrade
              </a> to remove it.
            </div>
          )}
          {renderStatus === "failed" && (
            <div data-testid="render-error" className="mt-3 text-xs font-mono text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2">
              Render failed. Try saving again and retry.
            </div>
          )}
          {hasVideoMedia && styleOpen && (
            <div
              data-testid="caption-style-picker"
              className="mt-4 border border-zinc-800 bg-zinc-950/60 p-4 grid grid-cols-2 md:grid-cols-3 gap-3"
            >
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Font</span>
                <select
                  value={style.font}
                  onChange={(e) => setStyle({ ...style, font: e.target.value })}
                  data-testid="style-font"
                  className="h-9 bg-zinc-950 border border-zinc-800 text-zinc-100 text-xs font-mono px-2"
                >
                  {["Arial", "Roboto", "Helvetica", "Georgia", "Verdana", "Courier New", "Impact", "Times New Roman"].map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Size · {style.size}px</span>
                <input
                  type="range"
                  min="14" max="48" step="1"
                  value={style.size}
                  onChange={(e) => setStyle({ ...style, size: Number(e.target.value) })}
                  data-testid="style-size"
                  className="h-9"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Position</span>
                <div className="inline-flex gap-px bg-zinc-800 border border-zinc-800" data-testid="style-position">
                  {["top", "middle", "bottom"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setStyle({ ...style, position: p })}
                      data-testid={`style-position-${p}`}
                      className={`flex-1 h-9 px-3 text-xs font-mono uppercase tracking-widest ${
                        style.position === p ? "bg-white text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Text colour</span>
                <input
                  type="color"
                  value={style.color}
                  onChange={(e) => setStyle({ ...style, color: e.target.value.toUpperCase() })}
                  data-testid="style-color"
                  className="h-9 w-full bg-zinc-950 border border-zinc-800 cursor-pointer"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Outline colour</span>
                <input
                  type="color"
                  value={style.outline_color}
                  onChange={(e) => setStyle({ ...style, outline_color: e.target.value.toUpperCase() })}
                  data-testid="style-outline-color"
                  className="h-9 w-full bg-zinc-950 border border-zinc-800 cursor-pointer"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Background</span>
                <div className="inline-flex gap-px bg-zinc-800 border border-zinc-800" data-testid="style-background">
                  {[
                    { k: "none", label: "None" },
                    { k: "outline", label: "Outline" },
                    { k: "box", label: "Box" },
                  ].map((b) => (
                    <button
                      key={b.k}
                      type="button"
                      onClick={() => setStyle({ ...style, background: b.k })}
                      data-testid={`style-background-${b.k}`}
                      className={`flex-1 h-9 px-3 text-xs font-mono uppercase tracking-widest ${
                        style.background === b.k ? "bg-white text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </label>
              {style.background === "box" && (
                <>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Box colour</span>
                    <input
                      type="color"
                      value={style.background_color}
                      onChange={(e) => setStyle({ ...style, background_color: e.target.value.toUpperCase() })}
                      data-testid="style-background-color"
                      className="h-9 w-full bg-zinc-950 border border-zinc-800 cursor-pointer"
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Box opacity · {style.background_opacity}%</span>
                    <input
                      type="range"
                      min="0" max="100" step="5"
                      value={style.background_opacity}
                      onChange={(e) => setStyle({ ...style, background_opacity: Number(e.target.value) })}
                      data-testid="style-background-opacity"
                      className="h-9"
                    />
                  </label>
                </>
              )}
              <div className="col-span-2 md:col-span-3 pt-2 border-t border-zinc-800">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Live preview</div>
                <div
                  data-testid="style-preview"
                  className="relative aspect-video max-w-md bg-gradient-to-br from-zinc-800 to-zinc-950 flex items-center justify-center border border-zinc-800"
                  style={{
                    padding: "0",
                  }}
                >
                  <div
                    className="absolute inset-0 flex"
                    style={{
                      alignItems: style.position === "top" ? "flex-start" : style.position === "middle" ? "center" : "flex-end",
                      justifyContent: "center",
                      padding: style.position === "middle" ? "0" : "16px",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: style.font,
                        fontSize: `${style.size / 2}px`,
                        color: style.color,
                        WebkitTextStroke: style.background === "outline" ? `${style.outline}px ${style.outline_color}` : "none",
                        backgroundColor: style.background === "box"
                          ? `${style.background_color}${Math.round((style.background_opacity / 100) * 255).toString(16).padStart(2, "0")}`
                          : "transparent",
                        padding: style.background === "box" ? "4px 8px" : "0",
                        textShadow: style.background === "outline" ? "none" : `1px 1px 0 ${style.outline_color}`,
                      }}
                    >
                      Sample caption text
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Segment list — scrolls independently of the sticky video. The
            active segment auto-scrolls into view during playback (see the
            useEffect on currentIdx above). */}
        <ScrollArea
          ref={listContainerRef}
          className="lg:h-[calc(100vh-4rem)] h-[560px]"
          data-testid="editor-segment-scroll"
        >
          <EditorMetricsStrip
            segments={segments}
            mediaSeconds={Number(job.media_seconds) || videoRef.current?.duration || 0}
            flaggedOnly={flaggedOnly}
            onToggleFlagged={setFlaggedOnly}
          />
          <div className="divide-y divide-zinc-800" data-testid="editor-segment-list">
            {/* Insert BEFORE the very first segment */}
            <div className="py-1 flex items-center justify-center gap-2 bg-zinc-950/40">
              <button
                type="button"
                data-testid="insert-segment-at-start-1"
                onClick={() => insertSegments(-1, 1)}
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-blue-400 border border-dashed border-zinc-800 hover:border-blue-500 px-3 py-1 opacity-60 hover:opacity-100 transition-all inline-flex items-center gap-1"
                title="Insert a new segment before segment #1"
              >
                <Plus className="w-3 h-3" /> Insert at start
              </button>
              <button
                type="button"
                data-testid="insert-segment-at-start-3"
                onClick={() => insertSegments(-1, 3)}
                className="text-[10px] font-mono text-zinc-600 hover:text-blue-400 px-2 py-1 opacity-60 hover:opacity-100"
                title="Insert 3 blank segments at the start"
              >
                +3
              </button>
            </div>
            {segments.map((s, idx) => {
              if (flaggedOnly && !cueHasBadges(s)) return null;
              const active = idx === currentIdx;
              const displayIdx = s.index ?? (idx + 1);
              const isLast = idx === segments.length - 1;
              return (
                <div key={`seg-wrap-${displayIdx}-${idx}`}>
                <div
                  key={`seg-${displayIdx}-${idx}`}
                  data-testid={`editor-segment-${displayIdx}`}
                  className={`p-4 md:px-6 transition-colors duration-150 ${active ? "bg-blue-500/10 border-l-2 border-blue-500" : "hover:bg-zinc-900/60"}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      type="button"
                      onClick={() => seekTo(idx)}
                      data-testid={`seek-segment-${displayIdx}`}
                      className="text-[10px] font-mono uppercase tracking-widest text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                    >
                      <Clock className="w-3 h-3" /> #{String(displayIdx).padStart(2, "0")} · {fmt(s.start)} → {fmt(s.end)}
                    </button>
                    <CueBadges seg={s} />
                    <span className="text-[10px] font-mono text-zinc-600 ml-auto">Click to jump</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mb-2 line-clamp-1" title={s.source_text}>
                    {s.source_text}
                  </div>
                  <Textarea
                    value={s.translated_text}
                    onChange={(e) => updateSegment(idx, { translated_text: e.target.value })}
                    data-testid={`edit-text-${s.index}`}
                    rows={2}
                    className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 resize-none text-sm leading-relaxed"
                  />
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      Start
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={s.start}
                        onChange={(e) => updateSegment(idx, { start: parseFloat(e.target.value) || 0 })}
                        data-testid={`edit-start-${s.index}`}
                        className="w-full mt-1 bg-zinc-950 border border-zinc-800 h-7 px-2 text-xs text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      End
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={s.end}
                        onChange={(e) => updateSegment(idx, { end: parseFloat(e.target.value) || 0 })}
                        data-testid={`edit-end-${s.index}`}
                        className="w-full mt-1 bg-zinc-950 border border-zinc-800 h-7 px-2 text-xs text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                </div>
                {!isLast ? (
                  <div className="relative py-1 group flex items-center justify-center gap-2">
                    <button
                      type="button"
                      data-testid={`insert-segment-after-${displayIdx}`}
                      onClick={() => insertSegments(idx, 1)}
                      className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-600 hover:text-blue-400 border border-dashed border-zinc-800 hover:border-blue-500 px-3 py-1 opacity-40 hover:opacity-100 transition-all"
                      title="Whisper missed a line here? Add one manually."
                    >
                      <Plus className="w-3 h-3" /> Insert segment
                    </button>
                    <button
                      type="button"
                      data-testid={`insert-segment-after-${displayIdx}-3`}
                      onClick={() => insertSegments(idx, 3)}
                      className="text-[10px] font-mono text-zinc-600 hover:text-blue-400 px-2 py-1 opacity-40 hover:opacity-100"
                      title="Insert 3 blank segments in this gap"
                    >
                      +3
                    </button>
                  </div>
                ) : (
                  <div className="py-1 flex items-center justify-center gap-2 bg-zinc-950/40">
                    <button
                      type="button"
                      data-testid={`insert-segment-at-end-1`}
                      onClick={() => insertSegments(idx, 1)}
                      className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-blue-400 border border-dashed border-zinc-800 hover:border-blue-500 px-3 py-1 opacity-60 hover:opacity-100 transition-all"
                      title="Add a new segment after the last one"
                    >
                      <Plus className="w-3 h-3" /> Insert at end
                    </button>
                    <button
                      type="button"
                      data-testid={`insert-segment-at-end-3`}
                      onClick={() => insertSegments(idx, 3)}
                      className="text-[10px] font-mono text-zinc-600 hover:text-blue-400 px-2 py-1 opacity-60 hover:opacity-100"
                      title="Insert 3 blank segments at the end"
                    >
                      +3
                    </button>
                  </div>
                )}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </section>
  );
}


// Keyboard shortcuts reference modal. Grouped by category so users can
// find what they need at a glance. Closes on Esc (Radix Dialog default),
// on click outside (also default), and on the explicit ✕ button.
function ShortcutsModal({ open, onClose }) {
  const groups = [
    {
      title: "Navigation",
      rows: [
        ["\u2191", "Previous segment"],
        ["\u2193", "Next segment"],
        ["Tab", "Next segment"],
        ["Shift + Tab", "Previous segment"],
      ],
    },
    {
      title: "Playback",
      rows: [
        ["Space", "Play / pause"],
        ["Shift + Space", "Replay current segment from start"],
        ["J", "Rewind 2 seconds"],
        ["L", "Forward 2 seconds"],
      ],
    },
    {
      title: "Editing",
      rows: [
        ["Enter", "Split segment at cursor position"],
        ["Delete", "Remove segment when its text is empty"],
        ["Ctrl / \u2318 + Z", "Undo last change"],
      ],
    },
    {
      title: "Timing",
      rows: [
        ["Shift + [", "Shift start time earlier by 0.1 s"],
        ["Shift + ]", "Shift end time later by 0.1 s"],
      ],
    },
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="shortcuts-modal"
        className="max-w-2xl bg-zinc-950 border-zinc-800 text-white rounded-none p-0 gap-0"
      >
        <DialogHeader className="p-6 border-b border-zinc-800">
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-blue-400" /> Keyboard shortcuts
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500 font-mono">
            All shortcuts work while typing in a subtitle. Space is intercepted
            for play/pause — paste (Ctrl + V) to insert a literal space in a caption.
          </DialogDescription>
        </DialogHeader>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {groups.map((g) => (
            <section key={g.title} data-testid={`shortcuts-group-${g.title.toLowerCase()}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">
                {g.title}
              </div>
              <ul className="space-y-2">
                {g.rows.map(([keys, desc]) => (
                  <li key={keys} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-zinc-300">{desc}</span>
                    <kbd className="inline-flex items-center px-2 py-0.5 border border-zinc-700 bg-zinc-900 text-[11px] font-mono text-zinc-200 rounded-sm whitespace-nowrap">
                      {keys}
                    </kbd>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

